import React, { useState } from 'react';
import { Button, Input, useDisclosure } from '@heroui/react';
import { Skeleton } from '@heroui/react';
import { useAuth } from '../hooks/useAuth';
import toast, { Toaster } from 'react-hot-toast';
import { useDispatch } from 'react-redux';
import * as authAction from '../redux/actions/authAction';
import { bindActionCreators, current } from '@reduxjs/toolkit';
import { FaRegEye, FaRegEyeSlash } from "react-icons/fa6";
import { UpdateProfileIcon } from '../utils/svgIcons';
import { removeToken } from '../utils/setAuthToken';
import { useNavigate } from 'react-router-dom';
import Modal from '../components/PopupModals/Modal';
import ModalBox from '../components/PopupModals/Modal';

function ChangePassword() {

    const navigate = useNavigate();
    const { user } = useAuth();
    const dispatch = useDispatch();
    const { changePassword } = bindActionCreators(authAction, dispatch);
    const [formData, setFormData] = useState({ old_password: '', new_password: '' });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [loading, setLoading] = useState(false);
    const toggleVisibility = () => setShowPassword(!showPassword);

    // Dynamic Modal
    const { isOpen, onOpen, onClose } = useDisclosure();
    // Modal End

    const validateFields = () => {
        let validationErrors = {};

        // Validate old password
        if (!formData.old_password) {
            validationErrors.old_password = 'Please enter your current password.';
        } else if (formData.old_password.length < 5) {
            validationErrors.old_password = 'Current password must be at least 5 characters long.';
        }

        // Validate new password
        if (!formData.new_password) {
            validationErrors.new_password = 'Please enter a new password.';
        } else if (formData.new_password.length < 6) {
            validationErrors.new_password = 'New password must be at least 6 characters.';
        } else if (!/[a-z]/.test(formData.new_password)) {
            validationErrors.new_password = 'New password must contain at least one lowercase letter.';
        } else if (!/[A-Z]/.test(formData.new_password)) {
            validationErrors.new_password = 'New password must contain at least one uppercase letter.';
        } else if (!/[0-9]/.test(formData.new_password)) {
            validationErrors.new_password = 'New password must contain at least one number.';
        } else if (!/[@$!%*?&]/.test(formData.new_password)) {
            validationErrors.new_password = 'New password must contain at least one special character (@, $, !, %, *, ?, &).';
        }

        setErrors(validationErrors);
        setLoading(false);

        return Object.keys(validationErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: '' });
    };

    const handleUpdate = () => {
        setLoading(true);
        if (!validateFields()) return;
        onOpen();
        setLoading(false);
    };

    const handleConfirmUpdate = () => {
        setIsSubmitting(true);

        onClose(onClose);

        changePassword(user._id, formData)
            .then((res) => {
                toast.success(res.data.message || 'Password updated successfully');
                setFormData({ old_password: '', new_password: '' });
                // Close modal and log user out after successful password change
                onClose(onClose);
                setTimeout(() => {
                    removeToken();
                    navigate("/login");
                }, 2000);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || 'Error updating password');
            })
            .finally(() => {
                setIsSubmitting(false);
                setLoading(false);
            });
    };


    return (
        <div className='flex flex-col'>
            <div className='flex flex-row justify-between items-center px-4 border-b py-4 border-gray-100'>
                <h3 className='text-lg font-medium'>Change Password</h3>
                <Button
                    // key={backdrop}
                    className='bg-button text-white'
                    isDisabled={isSubmitting}
                    startContent={<UpdateProfileIcon />}
                    onPress={handleUpdate}
                >
                    {isSubmitting ? 'Updating...' : 'Update'}
                </Button>
            </div>
            {loading ? (
                <div className='w-full flex gap-2 p-4'>
                    <Skeleton className='w-full h-14 rounded-lg' />
                    <Skeleton className='w-full h-14 rounded-lg' />
                </div>
            ) : (
                <div className='flex w-full flex-wrap md:flex-nowrap gap-4 p-4'>
                    <Input
                        label='Old Password'
                        type='password'
                        name='old_password'
                        value={formData.old_password}
                        onChange={handleChange}
                        isRequired
                        isInvalid={!!errors.old_password}
                        errorMessage={errors.old_password}
                    />
                    <Input
                        label='New Password'
                        type={showPassword ? "text" : "password"}
                        name='new_password'
                        value={formData.new_password}
                        onChange={handleChange}
                        isRequired
                        endContent={
                            <button type="button" onClick={toggleVisibility} className="focus:outline-none">
                                {showPassword ? <FaRegEyeSlash className="text-2xl text-gray-500" /> : <FaRegEye className="text-2xl text-gray-500" />}
                            </button>
                        }
                        isInvalid={!!errors.new_password}
                        errorMessage={errors.new_password}
                    />
                </div>
            )}
            <Toaster position='top-right' reverseOrder={false} />
            <ModalBox
                title={'Confirm Password Change'}
                isOpen={isOpen}
                onOpen={onOpen}
                onClose={onClose}
                handleConfirmUpdate={handleConfirmUpdate}
            >
                <div className="flex flex-col">
                    <p>Are you sure!</p>
                    <p>You want to <span className="text-amber-600 font-semibold">update your password?</span></p>
                </div>
            </ModalBox>
        </div>
    );
}

export default ChangePassword;
